import React from 'react';

import { AudioDetailsFragment } from '../../graphql/types';
import useTracking from '../../hooks/useTracking';
import {
  EventCategories,
  EventNames,
  EventProfileCardNames,
} from '../../utils/trackingEvents';
import { Accordion } from '../reusable/Accordion';
import Text, { TextKind } from '../reusable/Text';

export const AudioPlayer = ({ content }: { content: AudioDetailsFragment }) => {
  const [showTranscript, setShowTranscript] = React.useState(false);
  const { track } = useTracking();
  const lastValueRef = React.useRef<number>(1);
  const lastTimeRef = React.useRef<number>(0);
  const seekThreshold = 3; // seconds
  const audioRef = React.useRef<HTMLAudioElement>(null);

  const trackAudioEvent = (eventName: EventNames): void => {
    track(eventName, {
      content_id: content.id,
      card_name: EventProfileCardNames.ContentPreviewModal,
      event_category: EventCategories.Content,
    });
  };

  const handleContentPlay = () => {
    trackAudioEvent(EventNames.Play);
  };

  const handleContentPause = () => {
    trackAudioEvent(EventNames.Pause);
  };

  const handleTimeUpdate = (event: React.SyntheticEvent<HTMLAudioElement>) => {
    const { currentTime } = event.currentTarget;
    if (Math.abs(currentTime - lastTimeRef.current) > seekThreshold) {
      trackAudioEvent(EventNames.Seek);
    }
    lastTimeRef.current = currentTime;
  };

  const handleVolumeChange = (
    event: React.SyntheticEvent<HTMLAudioElement>,
  ) => {
    if (event.currentTarget.muted) {
      trackAudioEvent(EventNames.Mute);
    } else if (lastValueRef.current === 0 || lastValueRef.current === 1) {
      trackAudioEvent(EventNames.Unmute);
    }
    lastValueRef.current = event.currentTarget.muted ? 0 : 1;
  };

  return (
    <div>
      {/* biome-ignore lint/a11y/useMediaCaption: eslint migration */}
      <audio
        ref={audioRef}
        controls
        controlsList="nodownload"
        style={{ width: '100%', marginBottom: 8 }}
        data-testid="audioPlayer"
        onPlay={handleContentPlay}
        onPause={handleContentPause}
        onTimeUpdate={handleTimeUpdate}
        onVolumeChange={handleVolumeChange}
      >
        <source src={content.audio} type="audio/mpeg" />
      </audio>
      <div>
        {content.transcript && (
          <Accordion
            label={<Text>Transcript</Text>}
            open={showTranscript}
            onChange={() => setShowTranscript(!showTranscript)}
          >
            <Text
              kind={TextKind.BodyM}
              style={{ whiteSpace: 'pre-wrap', padding: 16 }}
            >
              {content.transcript}
            </Text>
          </Accordion>
        )}
      </div>
    </div>
  );
};
